import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import sanityClient from "../../sanityClient";

const POSTS_PER_PAGE = 8;

const InTheMedia = () => {
  const [mediaCoverage, setMediaCoverage] = useState([]);
  const [filteredCoverage, setFilteredCoverage] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "mediaCoverage"] | order(date desc) {
          _id,
          title,
          date,
          source,
          "url": link,
          "imageUrl": image.asset->url
        }`
      )
      .then((data) => {
        setMediaCoverage(data);
        setFilteredCoverage(data);
        setTotalPages(Math.ceil(data.length / POSTS_PER_PAGE));
      })
      .catch(console.error);
  }, []);

  const paginateCoverage = () => {
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    return filteredCoverage.slice(startIndex, endIndex);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    const filtered = mediaCoverage.filter(
      (item) =>
        item.title.toLowerCase().includes(term) ||
        item.source.toLowerCase().includes(term) ||
        new Date(item.date).toLocaleDateString().includes(term)
    );

    setFilteredCoverage(filtered);
    setTotalPages(Math.ceil(filtered.length / POSTS_PER_PAGE));
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <>
      <Helmet>
        <title>In The Media - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Discover the latest media coverage about Habitat for Humanity Buffalo. Stay updated with news, articles, and features highlighting our impact and community efforts."
        />
      </Helmet>
      <Container>
        <Header>In The Media</Header>

        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Search by title, source, or date..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </SearchBar>

        {filteredCoverage.length > 0 ? (
          <MediaGrid>
            {paginateCoverage().map((coverage) => (
              <MediaCard key={coverage._id}>
                {coverage.imageUrl && (
                  <MediaImage src={coverage.imageUrl} alt={coverage.title} />
                )}
                <MediaContent>
                  <MediaTitle>
                    <a
                      href={coverage.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {coverage.title}
                    </a>
                  </MediaTitle>
                  <MediaMeta>
                    {new Date(coverage.date).toLocaleDateString()} -{" "}
                    {coverage.source}
                  </MediaMeta>
                  <MediaLink
                    href={coverage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read the Article
                  </MediaLink>
                </MediaContent>
              </MediaCard>
            ))}
          </MediaGrid>
        ) : (
          <NoCoverage>No media coverage available.</NoCoverage>
        )}

        <Pagination>
          <PaginationButton
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </PaginationButton>
          {Array.from({ length: totalPages }, (_, i) => (
            <PageNumber
              key={i + 1}
              onClick={() => handlePageClick(i + 1)}
              isActive={currentPage === i + 1}
            >
              {i + 1}
            </PageNumber>
          ))}
          <PaginationButton
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </PaginationButton>
        </Pagination>
      </Container>
    </>
  );
};

// Styled Components
const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--container-padding);
  background-color: var(--background-color);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const Header = styled.h1`
  text-align: center;
  font-size: 1.8em;
  color: var(--primary-color);
  margin-bottom: 30px;
`;

const SearchBar = styled.div`
  margin-bottom: 20px;
  text-align: center;
`;

const SearchInput = styled.input`
  padding: 10px;
  font-size: 1em;
  width: 100%;
  max-width: 600px;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const MediaCard = styled.div`
  background-color: var(--secondary-color);
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  overflow: hidden;

  &:hover {
    transform: scale(1.02);
  }
`;

const MediaImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const MediaContent = styled.div`
  padding: 15px;
`;

const MediaTitle = styled.h2`
  font-size: 1.2em;
  margin: 0 0 10px;

  a {
    text-decoration: none;
    color: var(--primary-color);

    &:hover {
      text-decoration: underline;
    }
  }
`;

const MediaMeta = styled.p`
  font-size: 0.9em;
  color: #999;
  margin: 0 0 10px;
`;

const MediaLink = styled.a`
  display: inline-block;
  margin-top: 10px;
  color: var(--primary-color);
  text-decoration: underline;
`;

const NoCoverage = styled.p`
  text-align: center;
  font-size: 1em;
  color: #666;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

const PaginationButton = styled.button`
  background: var(--primary-color);
  color: #fff;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PageNumber = styled.span`
  cursor: pointer;
  padding: 5px 10px;
  font-size: 0.9em;
  font-weight: bold;
  color: ${({ isActive }) => (isActive ? "#fff" : "#000")};
  background-color: ${({ isActive }) =>
    isActive ? "var(--primary-color)" : "transparent"};
  border: ${({ isActive }) => (isActive ? "none" : "1px solid #ddd")};
  border-radius: 5px;

  &:hover {
    background-color: var(--primary-color);
    color: #fff;
  }
`;

export default InTheMedia;
